import api from './api';

/**
 * Matching rules + the generic engine — rules table, dry run, apply, undo.
 *
 * Since 3 Sep 2026 the rules apply to EVERY account, Interactive Brokers
 * included (the IB engine reads the same table for untagged fills). Tag →
 * system mappings for IB stay in ibSystemMappingService; the Automation page
 * renders both in one table.
 */
const autoMatchService = {
  /** Everything the rules table renders in one call. */
  async get() {
    const res = await api.get('/reconcile-rules');
    return res.data?.data;
  },

  /** The generic engine's opt-in switch. */
  async setEnabled(enabled) {
    const res = await api.patch('/reconcile-rules/settings', { auto_match_enabled: enabled });
    return res.data?.data;
  },

  /**
   * Create a rule ({ match_value?, instrument_type?, currency?, trading_system })
   * or set the fallback ({ match_type: 'default', trading_system: name | null }).
   * A null system on the fallback means "Wait in Review".
   */
  async createRule({ match_type = 'rule', match_value = null, instrument_type = null, currency = null, trading_system = null }) {
    const res = await api.post('/reconcile-rules', { match_type, match_value, instrument_type, currency, trading_system });
    return res.data?.data?.rule;
  },

  async updateRule(id, patch) {
    const res = await api.patch(`/reconcile-rules/${id}`, patch);
    return res.data?.data?.rule;
  },

  async deleteRule(id) {
    const res = await api.delete(`/reconcile-rules/${id}`);
    return res.data?.data;
  },

  /** Dry run — writes nothing. This is the last checkpoint before trades exist. */
  async preview(scope = 'all') {
    const res = await api.post('/reconcile-rules/preview', { scope });
    return res.data?.data;
  },

  /** Run the engine; with an accountId, only that account's rows (the import's last screen). */
  async apply(scope = 'all', accountId = null) {
    const res = await api.post('/reconcile-rules/apply', { scope, ...(accountId ? { accountId } : {}) });
    return res.data?.data;
  },

  async listRuns() {
    const res = await api.get('/reconcile-rules/runs');
    return res.data?.data?.runs || [];
  },

  async undoRun(runId) {
    const res = await api.post(`/reconcile-rules/runs/${runId}/undo`);
    return res.data?.data;
  }
};

export default autoMatchService;
